import { decompress } from "fzstd";
import JSZip from "jszip";
import initSqlJs, { type Database } from "sql.js";
import sqlWasmUrl from "sql.js/dist/sql-wasm.wasm?url";

export interface Flashcard {
	front: string;
	back: string;
}

export interface ImportedDeck {
	name: string;
	description?: string;
	flashcards: Flashcard[];
}

// Czyści HTML pola Anki, ale ZACHOWUJE formatowanie (listy, pogrubienia,
// kolory itd.) — fiszki trzymamy i renderujemy jako HTML (htmlFormatter),
// więc strip do czystego tekstu gubił style. Usuwamy tylko to, czego i tak
// nie odwzorujemy: media (numerowane pliki w archiwum, bez rozpakowania byłyby
// zepsute), skrypty/style oraz znaczniki dźwięku Anki [sound:...].
const cleanAnkiHtml = (html: string): string => {
	if (!html) return "";

	const div = document.createElement("div");
	div.innerHTML = html;
	div
		.querySelectorAll(
			"img, audio, video, source, style, script, object, iframe",
		)
		.forEach((el) => {
			el.remove();
		});

	return div.innerHTML.replace(/\[(sound|anki):[^\]]*\]/g, "").trim();
};

// Karta jest "pusta", gdy po usunięciu tagów nie zostaje żaden tekst.
const isBlankHtml = (html: string): boolean => {
	const div = document.createElement("div");
	div.innerHTML = html;
	return (div.textContent || "").trim().length === 0;
};

// Anki rozdziela poddecki znakiem "::" — bierzemy ostatni (najbardziej znaczący) segment
const cleanDeckName = (raw: string) => {
	const leaf = raw.split("::").pop() || raw;
	return leaf.trim();
};

// Parsuje plik .txt (front i tył oddzielone tabulatorem, linie z "#" pomijamy).
const parseTxt = async (blob: Blob): Promise<Flashcard[]> =>
	(await blob.text())
		.split("\n")
		.filter((line) => !line.startsWith("#") && line.trim())
		.map((line) => {
			const parts = line.split("\t");
			return {
				front: parts[0]?.trim() || "",
				back: parts[1]?.trim() || "",
			};
		})
		.filter((card) => card.front && card.back);

// Otwiera bazę kolekcji Anki z archiwum (.apkg / .colpkg).
// Nowsze eksporty (Anki 2.1.50+) trzymają SQLite jako `collection.anki21b`
// skompresowane algorytmem zstd; starsze — nieskompresowane `.anki21` / `.anki2`.
const openCollection = async (blob: Blob): Promise<Database> => {
	const zip = await JSZip.loadAsync(blob);

	const modernFile = zip.file("collection.anki21b");
	const legacyFile =
		zip.file("collection.anki21") || zip.file("collection.anki2");

	let dbData: Uint8Array;
	if (modernFile) {
		dbData = decompress(await modernFile.async("uint8array"));
	} else if (legacyFile) {
		dbData = await legacyFile.async("uint8array");
	} else {
		throw new Error("Invalid Anki file");
	}

	const SQL = await initSqlJs({ locateFile: () => sqlWasmUrl });
	return new SQL.Database(dbData);
};

// Wyciąga nazwę zestawu z bazy kolekcji Anki. Obsługuje stary schemat
// (decki jako JSON w tabeli `col`) oraz nowszy (osobna tabela `decks`).
const extractDeckName = (db: Database): string => {
	let topDid: string | null = null;
	try {
		const r = db.exec(
			"SELECT did FROM cards GROUP BY did ORDER BY COUNT(*) DESC LIMIT 1",
		);
		if (r.length && r[0].values.length) {
			topDid = String(r[0].values[0][0]);
		}
	} catch {
		// brak tabeli cards / inny schemat — lecimy dalej
	}

	// Stary schemat: decki jako JSON w kolumnie col.decks
	try {
		const r = db.exec("SELECT decks FROM col LIMIT 1");
		const raw = r[0]?.values?.[0]?.[0];
		if (raw) {
			const decks = JSON.parse(String(raw)) as Record<
				string,
				{ name?: string }
			>;
			const topName = topDid ? decks[topDid]?.name : undefined;
			if (topName && topName !== "Default") {
				return cleanDeckName(topName);
			}
			const named = Object.values(decks)
				.map((d) => d.name)
				.filter((n): n is string => !!n && n !== "Default");
			if (named.length) {
				return cleanDeckName(named[0]);
			}
		}
	} catch {
		// brak kolumny decks / niepoprawny JSON — próbujemy nowy schemat
	}

	// Nowy schemat: osobna tabela decks
	try {
		const r = db.exec(
			topDid
				? `SELECT name FROM decks WHERE id = ${topDid} LIMIT 1`
				: "SELECT name FROM decks WHERE name != 'Default' LIMIT 1",
		);
		const name = r[0]?.values?.[0]?.[0];
		if (name && String(name) !== "Default") {
			return cleanDeckName(String(name));
		}
	} catch {
		// brak tabeli decks — zwracamy pusto
	}

	return "";
};

// Mapa did (id decku) -> nazwa. Obsługuje stary schemat (JSON w col.decks)
// oraz nowszy (osobna tabela decks).
const getDeckNameMap = (db: Database): Record<string, string> => {
	const map: Record<string, string> = {};

	try {
		const r = db.exec("SELECT decks FROM col LIMIT 1");
		const raw = r[0]?.values?.[0]?.[0];
		if (raw) {
			const decks = JSON.parse(String(raw)) as Record<
				string,
				{ name?: string }
			>;
			for (const [id, deck] of Object.entries(decks)) {
				if (deck?.name) map[String(id)] = deck.name;
			}
		}
	} catch {
		// brak kolumny decks / niepoprawny JSON
	}

	try {
		const r = db.exec("SELECT id, name FROM decks");
		if (r.length) {
			for (const row of r[0].values) {
				map[String(row[0])] = String(row[1]);
			}
		}
	} catch {
		// brak tabeli decks
	}

	return map;
};

// Parsuje plik Anki (.apkg / .txt) jako pojedynczy zestaw — łączy wszystkie notatki.
export const parseAnkiFile = async (
	blob: Blob,
	fileName: string,
): Promise<{ flashcards: Flashcard[]; deckName: string }> => {
	const extension = fileName.toLowerCase().split(".").pop();

	if (extension === "txt") {
		return { flashcards: await parseTxt(blob), deckName: "" };
	}

	const db = await openCollection(blob);
	const deckName = extractDeckName(db);
	const results = db.exec("SELECT flds FROM notes");
	db.close();

	if (!results.length || !results[0].values.length) {
		return { flashcards: [], deckName };
	}

	const flashcards = results[0].values
		.map((row) => {
			const fields = String(row[0]).split("\x1f");
			return {
				front: cleanAnkiHtml(fields[0] || ""),
				back: cleanAnkiHtml(fields[1] || ""),
			};
		})
		.filter((card) => !isBlankHtml(card.front) && !isBlankHtml(card.back));

	return { flashcards, deckName };
};

// Rozbija plik Anki na osobne zestawy wg decku (dla .colpkg / .apkg z wieloma deckami).
// Pliki .txt nie zawierają informacji o decku — zwracają jeden zestaw bez nazwy.
export const parseAnkiDecks = async (
	blob: Blob,
	fileName: string,
): Promise<ImportedDeck[]> => {
	const extension = fileName.toLowerCase().split(".").pop();

	if (extension === "txt") {
		const flashcards = await parseTxt(blob);
		return flashcards.length ? [{ name: "", flashcards }] : [];
	}

	const db = await openCollection(blob);
	const nameMap = getDeckNameMap(db);

	let results: ReturnType<Database["exec"]> = [];
	try {
		// jeden wiersz na notatkę (GROUP BY n.id) z przypisanym deckiem karty
		results = db.exec(
			"SELECT n.flds, c.did FROM notes n JOIN cards c ON c.nid = n.id GROUP BY n.id",
		);
	} catch {
		// nietypowy schemat — brak podziału na decki
	}
	db.close();

	const byDeck = new Map<string, Flashcard[]>();
	if (results.length && results[0].values.length) {
		for (const row of results[0].values) {
			const fields = String(row[0]).split("\x1f");
			const front = cleanAnkiHtml(fields[0] || "");
			const back = cleanAnkiHtml(fields[1] || "");
			if (isBlankHtml(front) || isBlankHtml(back)) continue;

			const did = String(row[1]);
			if (!byDeck.has(did)) byDeck.set(did, []);
			byDeck.get(did)?.push({ front, back });
		}
	}

	return Array.from(byDeck.entries())
		.map(([did, flashcards]) => ({
			name: cleanDeckName(nameMap[did] || ""),
			flashcards,
		}))
		.filter((deck) => deck.flashcards.length > 0);
};
